import {createFileRoute, Link, Outlet, redirect} from '@tanstack/react-router';
import {useState} from 'react';
import {requireAuthBeforeLoad} from '@/helpers/requireAuthBeforeLoad';
import {Navbar} from '@/components/ui/navbar';
import {Typography} from '@/components/ui/typography';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import {Button} from '@/components/ui/button';
import {Bookmark, Calendar, Link2, Menu, Settings, X} from 'lucide-react';

export const Route = createFileRoute('/dashboard')({
  beforeLoad: ({context, location}) => {
    requireAuthBeforeLoad({context, location});

    if (
      location.pathname === '/dashboard' ||
      location.pathname === '/dashboard/'
    ) {
      throw redirect({to: '/dashboard/calendar'});
    }
  },
  component: DashboardLayout,
});

const navItemClass =
  'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors';

/** Sidebar navigation links shared between desktop and mobile drawer. */
function SidebarNav({onNavigate}: {onNavigate?: () => void}) {
  const navActiveClass = `${navItemClass} bg-neutral-50 text-neutral-700`;
  return (
    <nav className="space-y-1 h-full flex flex-col">
      <div className="h-full flex flex-col flex-1 p-6 space-y-1">
        <Link
          to="/dashboard/calendar"
          onClick={onNavigate}
          className={`${navItemClass} text-gray-700 hover:bg-gray-100`}
          activeProps={{
            className: navActiveClass,
          }}
        >
          <Calendar className="w-4 h-4" />
          Calendar
        </Link>
        <Link
          to="/dashboard/lists"
          onClick={onNavigate}
          className={`${navItemClass} text-gray-700 hover:bg-gray-100`}
          activeProps={{
            className: navActiveClass,
          }}
        >
          <Bookmark className="w-4 h-4" />
          Lists
        </Link>
        <Link
          to="/dashboard/connections"
          onClick={onNavigate}
          className={`${navItemClass} text-gray-700 hover:bg-gray-100`}
          activeProps={{
            className: navActiveClass,
          }}
        >
          <Link2 className="w-4 h-4" />
          Connections
        </Link>
      </div>
      <div className="border-t border-neutral-200" />
      <div className=" p-6 ">
        <Link
          to="/dashboard/settings"
          onClick={onNavigate}
          className={`${navItemClass} text-gray-700 hover:bg-gray-100`}
          activeProps={{
            className: navActiveClass,
          }}
        >
          <Settings className="w-4 h-4" />
          Settings
        </Link>
      </div>
    </nav>
  );
}

/**
 * Dashboard layout. Full-page width with sidebar filling page height
 * on desktop, mobile drawer on smaller screens. Matches admin blog layout.
 */
function DashboardLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      <Navbar />

      <div className="bg-white border-b border-neutral-200 flex-shrink-0 h-14">
        <div className="px-4 h-full flex items-center gap-3">
          <Drawer
            open={drawerOpen}
            onOpenChange={setDrawerOpen}
            direction="left"
          >
            <DrawerTrigger asChild>
              <Button variant="ghost" size="sm" className="lg:hidden -ml-2">
                <Menu className="w-5 h-5" aria-label="Open menu" />
              </Button>
            </DrawerTrigger>
            <DrawerContent>
              <DrawerHeader className="flex flex-row items-center justify-between">
                <DrawerTitle>Dashboard</DrawerTitle>
                <DrawerClose asChild>
                  <Button variant="ghost" size="sm">
                    <X className="w-5 h-5" aria-label="Close menu" />
                  </Button>
                </DrawerClose>
              </DrawerHeader>
              <SidebarNav onNavigate={() => setDrawerOpen(false)} />
            </DrawerContent>
          </Drawer>

          <div className="min-w-0 px-4">
            <Typography level="B1" className="truncate">
              Dashboard
            </Typography>
          </div>
        </div>
      </div>

      <div className="flex flex-1 w-full mx-auto min-w-0 overflow-hidden">
        <div className="flex gap-6 flex-1 min-w-0">
          <div className="hidden lg:block w-56 flex-shrink-0 bg-white border-r border-neutral-100 overflow-y-auto">
            <SidebarNav />
          </div>

          <div className="flex-1 min-w-0 pl-6 lg:pl-0 py-6 pr-6 flex flex-col overflow-y-auto">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}
